/**
 * Voice preview API — wrapper sobre `POST /api/voices/preview` que reusa la
 * BYOK key del user via el resolver del backend. Devuelve un Blob (audio/mpeg)
 * para reproducir con `new Audio(URL.createObjectURL(blob))`.
 *
 * Usado por:
 *   - `modules/fonix/ConfirmationModal.tsx` — click en una voz del catálogo.
 *   - (futuro) cualquier UI que muestre voiceIds sueltos (settings, admin).
 *
 * Para previewar la voz de una persona registrada en DB seguí usando
 * `personasApi.preview(personaId)` — comparten el helper backend pero la API
 * pública es distinta porque el caso de uso es distinto (persona = voz +
 * nombre + tono; voz suelta = sólo voiceId).
 */

export class VoicePreviewApiError extends Error {
  status: number;
  code?: string;
  constructor(message: string, status: number, code?: string) {
    super(message);
    this.status = status;
    if (code) this.code = code;
  }
}

/** Returns the audio Blob (audio/mpeg) for client-side playback. */
export async function previewVoice(voiceId: string, text?: string): Promise<Blob> {
  const res = await fetch('/api/voices/preview', {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(text ? { voiceId, text } : { voiceId }),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new VoicePreviewApiError(
      body.error ?? `HTTP ${res.status}`,
      res.status,
      body.error,
    );
  }
  return res.blob();
}
